// Firebase configuration for Yorm App
// Push notifications and real-time messaging
//
// PERF: Firebase SDK is heavy (~180 KB gzipped). We now DYNAMICALLY import
// `firebase/app` and `firebase/messaging` only when `initializeFirebase()`
// is actually called (i.e. right after login) — so the landing page and
// signed-out flows never pay the cost. This alone drops the main bundle
// by ~30% and moves Firebase to its own async chunk.

// Firebase configuration - from environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase
let app = null;
let messaging = null;
// Cached refs to the dynamically-loaded SDK helpers so we don't re-import
// every time onForegroundMessage / getToken is called on the same session.
let _sdk = null;

async function _loadSdk() {
  if (_sdk) return _sdk;
  // Split imports keep webpack's chunking clean: firebase-app + messaging
  // land in their own async chunks named by content hash.
  const [{ initializeApp }, msgMod] = await Promise.all([
    import(/* webpackChunkName: "firebase-app" */ "firebase/app"),
    import(/* webpackChunkName: "firebase-messaging" */ "firebase/messaging"),
  ]);
  _sdk = {
    initializeApp,
    getMessaging: msgMod.getMessaging,
    getToken: msgMod.getToken,
    onMessage: msgMod.onMessage,
  };
  return _sdk;
}

export const initializeFirebase = async () => {
  if (app) return { app, messaging };
  if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
    console.warn("Firebase config not found in environment variables");
    return { app: null, messaging: null };
  }
  try {
    const sdk = await _loadSdk();
    app = sdk.initializeApp(firebaseConfig);
    if ("Notification" in window && "serviceWorker" in navigator) {
      messaging = sdk.getMessaging(app);
    }
    return { app, messaging };
  } catch (error) {
    console.error("Firebase initialization error:", error);
    return { app: null, messaging: null };
  }
};

// Request notification permission and get FCM token
export const requestNotificationPermission = async () => {
  if (!("Notification" in window)) {
    console.log("This browser does not support notifications");
    return null;
  }
  try {
    const permission = await Notification.requestPermission();
    if (permission === "granted") {
      return await getFCMToken();
    }
    return null;
  } catch (error) {
    console.error("Error requesting notification permission:", error);
    return null;
  }
};

// Get FCM token
export const getFCMToken = async () => {
  if (!messaging) {
    const { messaging: msg } = await initializeFirebase();
    messaging = msg;
  }
  if (!messaging) return null;
  try {
    let registration = await navigator.serviceWorker.getRegistration("/firebase-messaging-sw.js");
    if (!registration) {
      registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
    }
    const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY || null;
    const tokenOptions = { serviceWorkerRegistration: registration };
    if (VAPID_KEY) tokenOptions.vapidKey = VAPID_KEY;
    const sdk = await _loadSdk();
    return await sdk.getToken(messaging, tokenOptions);
  } catch (error) {
    console.error("Error getting FCM token:", error);
    return null;
  }
};

// Register FCM token with backend
export const registerFCMToken = async (token, authToken) => {
  if (!token || !authToken) return false;
  try {
    const API = process.env.REACT_APP_BACKEND_URL;
    const response = await fetch(`${API}/api/fcm-token?token=${authToken}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: token, device_type: "web" }),
    });
    return response.ok;
  } catch (error) {
    console.error("Error registering FCM token:", error);
    return false;
  }
};

// Unregister FCM token on logout
export const unregisterFCMToken = async (fcmToken, authToken) => {
  if (!fcmToken || !authToken) return;
  try {
    const API = process.env.REACT_APP_BACKEND_URL;
    await fetch(`${API}/api/fcm-token?fcm_token=${fcmToken}&token=${authToken}`, {
      method: "DELETE",
    });
  } catch (error) {
    console.error("Error unregistering FCM token:", error);
  }
};

// Listen for foreground messages
export const onForegroundMessage = (callback) => {
  // Fire-and-forget: kick off async init but return a stub unsubscribe
  // immediately so callers can call this at render time without awaiting.
  let unsub = () => {};
  (async () => {
    if (!messaging) {
      const { messaging: msg } = await initializeFirebase();
      messaging = msg;
    }
    if (!messaging) return;
    const sdk = await _loadSdk();
    unsub = sdk.onMessage(messaging, (payload) => {
      callback(payload);
    });
  })();
  return () => unsub();
};

export { app, messaging };
